/**
 * Main-thread HTTP client that delegates all fetch() calls to a Web Worker.
 * Use this instead of global fetch() to avoid main-thread blocking and reduce
 * canceled requests when BLE/Serial traffic is heavy.
 */

import type { HttpWorkerError, HttpWorkerRequest, HttpWorkerResponse, HttpWorkerPongMessage } from './http-worker';

/** Response-like object returned by httpFetch so existing response.json() / .text() / .blob() code keeps working. */
export interface HttpResponseLike {
  ok: boolean;
  status: number;
  statusText: string;
  headers: Headers;
  json(): Promise<unknown>;
  text(): Promise<string>;
  blob(): Promise<Blob>;
  arrayBuffer(): Promise<ArrayBuffer>;
}

export type HttpFetchOptions = {
  method?: string;
  headers?: Record<string, string>;
  body?: string | null;
  /** How to interpret the response body. Default 'json'. */
  responseType?: 'json' | 'text' | 'arraybuffer';
  /** AbortSignal to cancel the request (e.g. from AbortController). */
  signal?: AbortSignal | null;
  /** Request timeout in milliseconds. Cancels the request if exceeded. */
  timeout?: number;
};

const PING_ID = -1;

let worker: Worker | null = null;
let nextId = 0;
let readyPromise: Promise<void> | null = null;
let readyResolver: (() => void) | null = null;
const pending = new Map<number, { resolve: (r: HttpResponseLike) => void; reject: (e: Error) => void }>();

type WorkerMessage = HttpWorkerResponse | HttpWorkerError | HttpWorkerPongMessage;

function isPong(data: WorkerMessage): data is HttpWorkerPongMessage {
  return 'type' in data && data.type === 'PONG';
}

function getWorker(): Worker {
  if (!worker) {
    worker = new Worker(new URL('./http-worker.ts', import.meta.url), { type: 'module' });
    readyPromise = new Promise<void>((resolve) => {
      readyResolver = resolve;
    });

    worker.onmessage = (event: MessageEvent<WorkerMessage>) => {
      const data = event.data;

      if (isPong(data)) {
        readyResolver?.();
        readyResolver = null;
        return;
      }

      const entry = pending.get(data.id);
      if (!entry) return;
      pending.delete(data.id);

      if ('error' in data) {
        entry.reject(new Error(data.error));
        return;
      }

      const res = data as HttpWorkerResponse;
      const headers = new Headers(res.headers);
      const body = res.body;

      const responseLike: HttpResponseLike = {
        ok: res.ok,
        status: res.status,
        statusText: res.statusText,
        headers,
        json: () => Promise.resolve(body as unknown),
        text: () => Promise.resolve(typeof body === 'string' ? body : JSON.stringify(body)),
        blob: () => Promise.resolve(body instanceof ArrayBuffer ? new Blob([body]) : new Blob([String(body)])),
        arrayBuffer: () => Promise.resolve(body instanceof ArrayBuffer ? body : new TextEncoder().encode(String(body)).buffer),
      };
      entry.resolve(responseLike);
    };
    worker.onerror = (event) => {
      const err = new Error(event.message || 'HTTP worker error');
      pending.forEach((entry) => entry.reject(err));
      pending.clear();
      readyResolver?.();
      readyResolver = null;
      readyPromise = null;
      nextId = 0;

      if (worker) {
        try {
          worker.terminate();
        } catch {
          // ignore if already terminated
        }
        worker = null;
      }
    };

    worker.postMessage({ type: 'PING', id: PING_ID });
  }
  return worker;
}

/** Returns a promise that resolves when the worker has responded to PING (ready to handle requests). */
function getReady(): Promise<void> {
  getWorker();
  return readyPromise ?? Promise.resolve();
}

/**
 * Terminate the HTTP worker and reject any pending requests. Call on app teardown
 * (e.g. beforeunload) to avoid leaving the worker running.
 */
export function destroy(): void {
  if (worker) {
    pending.forEach((entry) => entry.reject(new Error('HTTP worker destroyed')));
    pending.clear();
    try {
      worker.terminate();
    } catch {
      // ignore if already terminated
    }
    worker = null;
  }
  readyPromise = null;
  readyResolver = null;
  nextId = 0;
}

if (typeof window !== 'undefined') {
  window.addEventListener('beforeunload', destroy);
}

/**
 * Fetch a URL via the HTTP worker. Returns a Response-like object so you can use
 * .ok, .status, .json(), .text(), .blob(), .arrayBuffer() as with normal fetch().
 */
export function httpFetch(url: string, options: HttpFetchOptions = {}): Promise<HttpResponseLike> {
  const { method = 'GET', headers = {}, body = null, responseType = 'json', signal = null, timeout } = options;

  if (signal?.aborted) {
    return Promise.reject(new DOMException('The operation was aborted.', 'AbortError'));
  }

  return getReady().then(() => {
    const w = getWorker();
    if (!w) {
      return Promise.reject(new Error('HTTP worker not available'));
    }

    const id = nextId++;
    let timeoutId: ReturnType<typeof setTimeout> | undefined;
    const abortError = new DOMException('The operation was aborted.', 'AbortError');

    const onAbort = () => {
      if (timeoutId != null) clearTimeout(timeoutId);
      timeoutId = undefined;
      pending.delete(id);
      w.postMessage({ type: 'CANCEL_REQUEST', id });
    };

    return new Promise<HttpResponseLike>((resolve, reject) => {
      const abortListener = () => {
        if (timeoutId != null) clearTimeout(timeoutId);
        signal?.removeEventListener('abort', abortListener);
        onAbort();
        reject(abortError);
      };

      const cleanup = () => {
        if (timeoutId != null) clearTimeout(timeoutId);
        signal?.removeEventListener('abort', abortListener);
      };

      if (signal) {
        signal.addEventListener('abort', abortListener, { once: true });
      }

      if (timeout != null && timeout > 0) {
        timeoutId = setTimeout(() => {
          if (pending.has(id)) {
            onAbort();
            reject(new Error(`Request timeout after ${timeout}ms`));
          }
        }, timeout);
      }

      pending.set(id, {
        resolve: (r) => {
          cleanup();
          pending.delete(id);
          resolve(r);
        },
        reject: (e) => {
          cleanup();
          pending.delete(id);
          reject(e);
        },
      });

      const request: HttpWorkerRequest = { id, url, method, headers, body, responseType };
      w.postMessage(request);
    });
  });
}
